<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';

  import { DocumentUploadForm } from '@client/pages/manage/assets/modals/shared';

  import { AssetsService } from '@client/services/assets';

  import { appStore } from '@client/stores/app';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let currentRulenames = [];
  let invalidRulename = false;
  let fileData = {};
  let files = [];
  let noncompliant = false;
  let rulename = '';
  let uploadingFile = false;
  let viewonly = false;

  let pdfViewer;
  let pageNumber = 0;
  let totalPages = 0;
  let isPdfLoaded = false;

  const onRemoveFile = () => {
    files = [];
    fileData = {};
    uploadingFile = false;
  };

  const goToPage = (page) => {
    pdfViewer.goToPage(page);
  };

  const handlePageChanged = (event) => {
    pageNumber = event.detail.pageNumber;
  };

  const handleLoadedSuccess = (event) => {
    totalPages = event.detail.totalPages;
    pageNumber = 1;
    isPdfLoaded = true;
  };

  const onFileUpload = async () => {
    uploadingFile = true;

    const file = new FormData();
    file.append('file', files[0]);

    const response = await AssetsService.uploadFile(files[0].name, files[0].type, file);

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: files[0].name } }), 'error');
      throw new Error($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: files[0].name } }));
    }

    const { hash, filesize, filename } = response;

    fileData = { ...fileData, hash, filesize, filename };

    uploadingFile = false;
  };

  const executeCreateDocument = async () => {
    $appStore.loading = true;

    await onFileUpload();

    const data = {
      rulename: rulename,
      noncompliant: noncompliant,
      viewonly: viewonly,
      hash: fileData.hash,
      filesize: fileData.filesize,
      filename: fileData.filename
    };

    const response = await AssetsService.addDocument(data);

    $appStore.loading = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: rulename } }), 'error');
      throw new Error($format('label.FAILURE.CREATE_DOCUMENT'));
    }

    notify($format('label.SUCCESS.ASSET_UPLOAD', { values: { asset: rulename } }));

    step.data = {
      id: rulename
    };

    return response;
  };

  const rollbackCreateDocument = async () => {
    if (!step.data?.id) {
      return;
    }

    $appStore.loading = true;

    try {
      const documents = await AssetsService.getAllDocuments();
      const document = documents.find((doc) => doc.rulename === step.data.id);

      if (document) {
        await AssetsService.deleteDocument(document);
      }
    } catch (error) {
      console.error('Failed to rollback document creation:', error);
    } finally {
      $appStore.loading = false;
    }
  };

  const getCurrentRulenames = async () => {
    const response = await AssetsService.getAllDocuments();
    currentRulenames = response.map(({ rulename }) => rulename);
  };

  onMount(() => {
    getCurrentRulenames();

    if (onExecute) {
      onExecute(executeCreateDocument);
    }
    if (onRollback) {
      onRollback(rollbackCreateDocument);
    }
  });

  $: invalidRulename = currentRulenames.includes(rulename.toLowerCase());
  $: nextDisabled = !files.length || uploadingFile || invalidRulename || !rulename.length;
</script>

<div class="document-create-form">
  <DocumentUploadForm
    bind:rulename
    bind:files
    bind:viewonly
    bind:noncompliant
    bind:invalidRulename
    bind:currentRulenames
    bind:pdfViewer
    bind:pageNumber
    bind:totalPages
    bind:isPdfLoaded
    {onRemoveFile}
    {goToPage}
    {handlePageChanged}
    {handleLoadedSuccess}
    inputId="workflow-document-create"
  />
</div>

<style lang="scss">
  .document-create-form {
    width: 100%;
  }
</style>
